// ═══════════════════════════════════════════════════════════════
// DEBRIS ADMS — Load Tickets CRUD
// The core operational page — tracks every load from pickup to disposal
// ═══════════════════════════════════════════════════════════════

import { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { usePolling } from '../../hooks/usePolling';
import {
  Title, Text, Paper, Group, Badge, Button, TextInput, Select, Switch,
  Textarea, ActionIcon, Modal, Stack, SimpleGrid, Divider,
} from '../../components/gab/GabComponents';
import {
  IconSearch, IconPlus, IconPencil, IconTrash, IconRefresh,
  IconCamera, IconX, IconPrinter, IconCurrentLocation, IconMapPin, IconPhoto, IconArrowBackUp,
} from '@tabler/icons-react';
import QRCode from 'qrcode';
import { notifications } from '../../lib/notifications';

import { useDebrisOpsStore } from '../../store/debrisOpsStore';
import { useDebrisStore } from '../../store/debrisStore';
import { useAuthStore } from '../../store/authStore';
import { useModulePermission } from '../../hooks/useModulePermission';
import { useIsAdmin } from '../../hooks/useIsAdmin';
import { DEBRIS_TYPE_LABELS, DEBRIS_TYPE_COLORS, DEBRIS_TYPES, DEBRIS_TYPE_OPTIONS, TICKET_STATUS_LABELS } from '../../types/debris';
import type { DebrisLoadTicket, DebrisType, TicketStatus } from '../../types/debris';
import { fetchTable, TABLES, FIELDS, WORKER_URL } from '../../lib/api-switch';

// ── Compact signature pad (restores from saved base64 on mount) ──
function SignaturePad({ label, value, onCapture }: { label: string; value?: string; onCapture: (dataUrl: string) => void }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [drawing, setDrawing] = useState(false);
  const [signed, setSigned] = useState(false);
  const getCtx = () => canvasRef.current?.getContext('2d') || null;

  // Restore saved signature on mount
  useEffect(() => {
    if (value && canvasRef.current) {
      const ctx = canvasRef.current.getContext('2d');
      if (ctx) {
        const img = new Image();
        img.onload = () => {
          ctx.clearRect(0, 0, canvasRef.current!.width, canvasRef.current!.height);
          ctx.drawImage(img, 0, 0, canvasRef.current!.width, canvasRef.current!.height);
          setSigned(true);
        };
        img.src = value;
      }
    }
  }, []); // only on mount

  const startDraw = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    const ctx = getCtx(); if (!ctx) return;
    setDrawing(true);
    const rect = canvasRef.current!.getBoundingClientRect();
    const x = 'touches' in e ? e.touches[0].clientX - rect.left : e.nativeEvent.offsetX;
    const y = 'touches' in e ? e.touches[0].clientY - rect.top : e.nativeEvent.offsetY;
    ctx.beginPath(); ctx.moveTo(x, y);
  }, []);
  const draw = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    if (!drawing) return; const ctx = getCtx(); if (!ctx) return;
    const rect = canvasRef.current!.getBoundingClientRect();
    const x = 'touches' in e ? e.touches[0].clientX - rect.left : e.nativeEvent.offsetX;
    const y = 'touches' in e ? e.touches[0].clientY - rect.top : e.nativeEvent.offsetY;
    ctx.lineWidth = 2; ctx.strokeStyle = '#000'; ctx.lineCap = 'round'; ctx.lineTo(x, y); ctx.stroke();
  }, [drawing]);
  const endDraw = useCallback(() => {
    setDrawing(false);
    if (canvasRef.current) { onCapture(canvasRef.current.toDataURL('image/png')); setSigned(true); }
  }, [onCapture]);
  const clear = () => {
    const ctx = getCtx();
    if (ctx && canvasRef.current) { ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height); setSigned(false); onCapture(''); }
  };
  return (
    <div>
      <Group justify="space-between" mb={4}>
        <Text size="xs" fw={600}>{label}</Text>
        {signed && <Badge size="xs" color="green">Signed</Badge>}
      </Group>
      <canvas ref={canvasRef} width={400} height={140}
        style={{ border: '1px solid #e5e7eb', borderRadius: 6, cursor: 'crosshair', touchAction: 'none', background: '#fff', width: '100%', maxWidth: 400 }}
        onMouseDown={startDraw} onMouseMove={draw} onMouseUp={endDraw} onMouseLeave={endDraw}
        onTouchStart={startDraw} onTouchMove={draw} onTouchEnd={endDraw} />
      <Button size="compact-xs" variant="subtle" mt={2} onClick={clear}>Clear</Button>
    </div>
  );
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.readAsDataURL(file);
  });
}

const STATUS_OPTIONS = Object.entries(TICKET_STATUS_LABELS).map(([v, l]) => ({ value: v, label: l }));

const STATUS_COLORS: Record<string, string> = {
  draft: 'gray', in_transit: 'blue', at_site: 'yellow', verified: 'green',
  delivered: 'teal', disputed: 'red', voided: 'gray',
};

const DEBRIS_TYPE_SELECT = DEBRIS_TYPE_OPTIONS;

const EMPTY: Partial<DebrisLoadTicket> = {
  ticketNumber: '', truckId: '', truckNumber: '', crewId: '', contractorId: '',
  zoneId: '', disposalSiteId: '', debrisType: '', loadPercentage: '',
  calculatedCubicYards: '', pickupLat: '', pickupLng: '', pickupAddress: '',
  pickupTime: '', disposalTime: '', monitorName: '', status: 'in_transit', notes: '',
};

export default function LoadTickets() {
  const { activeIncident } = useAuthStore();
  const { canEdit } = useModulePermission('debris');
  const isAdmin = useIsAdmin();
  const incidentId = activeIncident?.id?.toString() ?? '';

  const {
    loadTickets: tickets, loadingTickets,
    loadLoadTickets, createLoadTicket, updateLoadTicket, deleteLoadTicket,
  } = useDebrisOpsStore();

  const {
    contractors, crews, trucks, zones, disposalSites,
    loadContractors, loadCrews, loadTrucks, loadZones, loadDisposalSites,
  } = useDebrisStore();

  const [searchParams] = useSearchParams();
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [typeFilter, setTypeFilter] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 25;
  const [sortField, setSortField] = useState<string>('');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const toggleSort = (field: string) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };
  const sortIcon = (f: string) => sortField === f ? (sortDir === 'asc' ? ' ↑' : ' ↓') : '';
  const [modalOpen, setModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<DebrisLoadTicket | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<DebrisLoadTicket | null>(null);
  const [form, setForm] = useState<Partial<DebrisLoadTicket>>({ ...EMPTY });
  const [saving, setSaving] = useState(false);

  // Photo state — only load photo at ticket level (before/after now per-stop)
  const [loadPhoto, setLoadPhoto] = useState<{ data: string; name: string } | null>(null);

  // View-only detail
  const [viewTicket, setViewTicket] = useState<DebrisLoadTicket | null>(null);

  // QR code display
  const [showQRForTicket, setShowQRForTicket] = useState<number | null>(null);
  const [qrDataUrl, setQrDataUrl] = useState<string>('');

  // Monitor options for loading monitor dropdown
  const [monitorOptions, setMonitorOptions] = useState<Array<{ value: string; label: string }>>([]);

  // Stop address autocomplete (shared state — one active at a time)
  const [stopAddrResults, setStopAddrResults] = useState<Array<{ label: string; lat: string; lng: string }>>([]);
  const [stopAddrActiveIdx, setStopAddrActiveIdx] = useState<number>(-1);
  const stopAddrTimer = useMemo(() => ({ current: null as ReturnType<typeof setTimeout> | null }), []);
  const fetchStopAddr = (q: string, stopIdx: number) => {
    setStopAddrActiveIdx(stopIdx);
    if (stopAddrTimer.current) clearTimeout(stopAddrTimer.current);
    if (q.length < 3) { setStopAddrResults([]); return; }
    stopAddrTimer.current = setTimeout(async () => {
      try {
        const r = await fetch(`${WORKER_URL}/api/here/autosuggest?q=${encodeURIComponent(q)}&at=33.749,-84.388&limit=5`, {
          headers: { 'Origin': 'https://apps.opengov.com' },
        });
        if (!r.ok) return;
        const data = await r.json();
        setStopAddrResults((data.items || []).filter((i: any) => i.address?.label || i.title).map((i: any) => ({
          label: i.address?.label || i.title,
          lat: i.position ? String(i.position.lat) : '',
          lng: i.position ? String(i.position.lng) : '',
        })));
      } catch { setStopAddrResults([]); }
    }, 250);
  };

  const handleLoadPhotoSelect = async (file: File | null) => {
    if (!file) return;
    const data = await fileToBase64(file);
    setLoadPhoto({ data, name: file.name });
  };

  const clearPhotos = () => { setLoadPhoto(null); };

  useEffect(() => {
    loadLoadTickets(); loadContractors(); loadCrews(); loadTrucks(); loadZones(); loadDisposalSites();
    if (TABLES.debrisMonitors) {
      const FM = FIELDS.debrisMonitors;
      fetchTable(TABLES.debrisMonitors, { limit: 200 }).then((rows: any[]) => {
        const seen = new Set<string>();
        const opts: { value: string; label: string }[] = [];
        for (const m of rows) {
          if (String(m[FM.status] ?? 'active') !== 'active') continue;
          const name = String(m[FM.fullName] ?? '').trim();
          if (!name || seen.has(name)) continue;
          seen.add(name);
          const company = String(m[FM.company] ?? '');
          opts.push({ value: name, label: company ? `${name} — ${company}` : name });
        }
        setMonitorOptions(opts);
      }).catch(() => {});
    }
  }, [loadLoadTickets, loadContractors, loadCrews, loadTrucks, loadZones, loadDisposalSites]);
  usePolling(() => { loadLoadTickets(); loadTrucks(); loadDisposalSites(); });

  const iTickets = tickets;
  const iContractors = incidentId ? contractors : [];
  const iTrucks = incidentId ? trucks : [];
  const iCrews = incidentId ? crews : [];
  const iZones = incidentId ? zones : [];
  const iSites = incidentId ? disposalSites : [];

  const contractorSelect = useMemo(() => iContractors.map(c => ({ value: String(c.id), label: c.companyName || `#${c.id}` })), [iContractors]);
  const truckSelect = useMemo(() => iTrucks.map(t => ({ value: String(t.id), label: `${t.truckNumber} (${t.cubicYardCapacity || '?'} CY)` })), [iTrucks]);
  // Lookup map: truckId (number) → truckNumber string
  const truckMap = useMemo(() => {
    const m = new Map<number, string>();
    iTrucks.forEach(tr => { if (tr.id) m.set(Number(tr.id), tr.truckNumber || ''); });
    return m;
  }, [iTrucks]);
  const crewSelect = useMemo(() => iCrews.map(c => ({ value: String(c.id), label: c.crewName || `#${c.id}` })), [iCrews]);
  const zoneSelect = useMemo(() => iZones.map(z => ({ value: String(z.id), label: z.zoneName || z.zoneCode || `#${z.id}` })), [iZones]);
  const siteSelect = useMemo(() => iSites.map(s => ({ value: String(s.id), label: s.siteName || `#${s.id}` })), [iSites]);

  const filtered = useMemo(() => {
    let list = iTickets;
    if (statusFilter && statusFilter !== 'all') list = list.filter(t => t.status === statusFilter);
    if (typeFilter && typeFilter !== 'all') list = list.filter(t => t.debrisType === typeFilter);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(t =>
        (t.ticketNumber || '').toLowerCase().includes(q) ||
        (truckMap.get(Number(t.truckId)) || t.truckNumber || '').toLowerCase().includes(q) ||
        (t.pickupAddress || '').toLowerCase().includes(q),
      );
    }
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...list].sort((a, b) => {
      if (!sortField) return (b.id || 0) - (a.id || 0); // newest first
      const va = (a as any)[sortField] ?? '';
      const vb = (b as any)[sortField] ?? '';
      const numA = Number(va); const numB = Number(vb);
      if (!isNaN(numA) && !isNaN(numB) && va !== '' && vb !== '') return (numA - numB) * dir;
      return String(va).localeCompare(String(vb)) * dir;
    });
  }, [iTickets, statusFilter, typeFilter, search, sortField, sortDir]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageRows = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // Reset to page 1 when filters change
  useEffect(() => { setPage(1); }, [statusFilter, typeFilter, search]);

  const zoneName = (id: string) => zones.find(z => String(z.id) === id)?.zoneName ?? id;
  const siteName = (id: string) => disposalSites.find(s => String(s.id) === id)?.siteName ?? id;
  // Aggregate totals from pickup stops
  const aggregateFromStops = (stopsJson: string, prev: Partial<DebrisLoadTicket>) => {
    try {
      const stops: any[] = JSON.parse(stopsJson || '[]');
      let totalCY = 0;
      let totalUnits = 0;
      let totalWeight = 0;
      for (const s of stops) {
        const cfg = s.debrisType ? DEBRIS_TYPES[s.debrisType as DebrisType] : null;
        const mode = cfg?.measurementMode || 'volumetric';
        if (mode === 'volumetric') totalCY += parseFloat(s.unitCount || '0');
        else if (mode === 'unit') totalUnits += parseFloat(s.unitCount || '0');
        else if (mode === 'weight') totalWeight += parseFloat(s.weight || '0');
      }
      const patch: Partial<DebrisLoadTicket> = {};
      if (totalCY) {
        patch.calculatedCubicYards = totalCY.toString();
        patch.loadPercentage = computeLoadPct(totalCY.toString(), prev.truckId ?? '');
      } else if (!totalCY && !prev.calculatedCubicYards) {
        patch.calculatedCubicYards = '';
        patch.loadPercentage = '';
      }
      if (totalUnits) patch.unitCount = totalUnits.toString();
      if (totalWeight) patch.weight = totalWeight.toString();
      return patch;
    } catch { return {}; }
  };

  const setField = (key: keyof DebrisLoadTicket, value: string) => {
    setForm(prev => {
      const next = { ...prev, [key]: value };
      // If stops changed, re-aggregate totals
      if (key === 'pickupStopsJson') {
        const agg = aggregateFromStops(value, next);
        return { ...next, ...agg };
      }
      return next;
    });
  };

  // Auto-generate ticket number
  const nextTicket = () => {
    const max = iTickets.reduce((m, t) => {
      const n = parseInt(t.ticketNumber?.replace(/\D/g, '') || '0');
      return n > m ? n : m;
    }, 0);
    return `T-${String(max + 1).padStart(5, '0')}`;
  };

  // Compute load% from CY and truck capacity (derived, not stored independently)
  const computeLoadPct = (cy: string, truckId: string) => {
    const truck = trucks.find(t => String(t.id) === truckId);
    if (!truck) return '';
    const cap = parseFloat(truck.cubicYardCapacity || '0');
    const cyVal = parseFloat(cy || '0');
    return cap && cyVal ? Math.round((cyVal / cap) * 100).toString() : '';
  };

  const handleTruckChange = (truckId: string) => {
    const truck = trucks.find(t => String(t.id) === truckId);
    setForm(prev => ({
      ...prev,
      truckId,
      truckNumber: truck?.truckNumber ?? '',
      contractorId: truck?.contractorId ?? prev.contractorId ?? '',
      loadPercentage: computeLoadPct(prev.calculatedCubicYards ?? '', truckId),
    }));
  };

  const openCreate = () => {
    setEditTarget(null);
    setForm({ ...EMPTY, ticketNumber: nextTicket() });
    clearPhotos();
    setModalOpen(true);
  };
  const openEdit = (t: DebrisLoadTicket) => { setEditTarget(t); setForm({ ...t }); clearPhotos(); setModalOpen(true); };

  // Resize stop photos to tiny thumbnails before persisting (Ignatius TEXT fields cap at ~65K)
  const compactStopsForSave = async (stopsJson: string): Promise<string> => {
    try {
      const stops: any[] = JSON.parse(stopsJson || '[]');
      const compacted = await Promise.all(stops.map(async (s: any) => {
        const out = { ...s };
        // Resize photos to 150px thumbnails (~2-4KB each) so JSON stays under 65K
        if (out.beforePhotoData && out.beforePhotoData.length > 5000) {
          try {
            const { resizeBase64ToThumb } = await import('../intake/lib/helpers');
            out.beforePhotoData = await resizeBase64ToThumb(out.beforePhotoData, 150, 0.4);
          } catch { out.beforePhotoData = ''; }
        }
        if (out.afterPhotoData && out.afterPhotoData.length > 5000) {
          try {
            const { resizeBase64ToThumb } = await import('../intake/lib/helpers');
            out.afterPhotoData = await resizeBase64ToThumb(out.afterPhotoData, 150, 0.4);
          } catch { out.afterPhotoData = ''; }
        }
        return out;
      }));
      return JSON.stringify(compacted);
    } catch { return stopsJson; }
  };

  const handleSave = async () => {
    if (!form.ticketNumber?.trim()) {
      notifications.show({ message: 'Ticket number is required', color: 'yellow' });
      return;
    }
    setSaving(true);
    try {
      // Compact stop photos before saving
      const compactedStops = form.pickupStopsJson
        ? await compactStopsForSave(form.pickupStopsJson)
        : '';
      const payload = { ...form, incidentId, pickupStopsJson: compactedStops };
      let ticketId: number;
      if (editTarget) {
        await updateLoadTicket(editTarget.id, payload);
        ticketId = editTarget.id;
        notifications.show({ message: 'Ticket updated', color: 'green' });
      } else {
        const created = await createLoadTicket(payload);
        ticketId = created?.id ?? 0;
        notifications.show({ message: 'Ticket created', color: 'green' });
      }

      // Store load photo in ticket meta (photoLoadUrl has no V2 column)
      if (ticketId && loadPhoto) {
        await updateLoadTicket(ticketId, { photoLoadUrl: loadPhoto.data });
      }

      setModalOpen(false);
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Save failed', color: 'red' });
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setSaving(true);
    try {
      await deleteLoadTicket(deleteTarget.id);
      notifications.show({ message: `Ticket ${deleteTarget.ticketNumber} deleted`, color: 'gray' });
      setDeleteTarget(null);
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Delete failed', color: 'red' });
    } finally { setSaving(false); }
  };

  return (
    <div>
      <Group justify="space-between" mb="md">
        <Title order={3} c="black">Load Tickets</Title>
        <Group gap="xs">
          <Button size="xs" variant="light" leftSection={<IconRefresh size={14} />}
            loading={loadingTickets} onClick={() => loadLoadTickets()}>Refresh</Button>
          {canEdit && <Button size="xs" leftSection={<IconPlus size={14} />} onClick={openCreate}>New Ticket</Button>}
        </Group>
      </Group>

      <Group mb="md" gap="sm">
        <TextInput placeholder="Search ticket #, truck, address..."
          leftSection={<IconSearch size={14} />} value={search}
          onChange={e => setSearch(e.target.value)} size="xs"
          style={{ flex: 1, minWidth: 200 }} />
        <Select data={[{ value: 'all', label: 'All Statuses' }, ...STATUS_OPTIONS]}
          value={statusFilter ?? 'all'} onChange={v => setStatusFilter(v)} size="xs" w={160} />
        <Select data={[{ value: 'all', label: 'All Types' }, ...DEBRIS_TYPE_SELECT]}
          value={typeFilter ?? 'all'} onChange={v => setTypeFilter(v)} size="xs" w={180} />
      </Group>

      <Text size="xs" c="dimmed" mb="xs">{filtered.length} ticket{filtered.length !== 1 ? 's' : ''}</Text>

      <Paper withBorder>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--mantine-color-gray-3)' }}>
                <th style={{ padding: '8px 12px', textAlign: 'left', cursor: 'pointer' }} onClick={() => toggleSort('ticketNumber')}>Ticket{sortIcon('ticketNumber')}</th>
                <th style={{ padding: '8px 12px', textAlign: 'left', cursor: 'pointer' }} onClick={() => toggleSort('truckNumber')}>Truck{sortIcon('truckNumber')}</th>
                <th style={{ padding: '8px 12px', textAlign: 'left', cursor: 'pointer' }} onClick={() => toggleSort('debrisType')}>Type{sortIcon('debrisType')}</th>
                <th style={{ padding: '8px 12px', textAlign: 'right', cursor: 'pointer' }} onClick={() => toggleSort('loadPercent')}>Load%{sortIcon('loadPercent')}</th>
                <th style={{ padding: '8px 12px', textAlign: 'right', cursor: 'pointer' }} onClick={() => toggleSort('cubicYards')}>CY{sortIcon('cubicYards')}</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Zone</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Site</th>
                <th style={{ padding: '8px 12px', textAlign: 'left', cursor: 'pointer' }} onClick={() => toggleSort('status')}>Status{sortIcon('status')}</th>
                <th style={{ padding: '8px 12px', textAlign: 'left', cursor: 'pointer' }} onClick={() => toggleSort('pickupTime')}>Pickup{sortIcon('pickupTime')}</th>
                <th style={{ padding: '8px 12px', textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={10} style={{ padding: 24, textAlign: 'center', color: '#888' }}>
                  {loadingTickets ? 'Loading...' : 'No tickets found'}
                </td></tr>
              )}
              {pageRows.map(t => {
                const isLocked = ['verified', 'delivered', 'voided'].includes(t.status) && !isAdmin;
                return (
                <tr key={t.id} style={{ borderBottom: '1px solid var(--mantine-color-gray-2)', cursor: 'pointer' }}
                  onClick={() => isLocked ? setViewTicket(t) : (canEdit ? openEdit(t) : setViewTicket(t))}>
                  <td style={{ padding: '8px 12px', fontWeight: 500 }}>
                    <Group gap={6} wrap="nowrap">
                      <Text size="sm" fw={500}>{t.ticketNumber || `#${t.id}`}</Text>
                      {t.status === 'draft' && <Badge size="xs" color="gray" variant="light">Draft</Badge>}
                    </Group>
                  </td>
                  <td style={{ padding: '8px 12px' }}>{truckMap.get(Number(t.truckId)) || t.truckNumber || '-'}</td>
                  <td style={{ padding: '8px 12px' }}>
                    <Badge size="sm" color={DEBRIS_TYPE_COLORS[t.debrisType as DebrisType] ?? 'gray'}>
                      {DEBRIS_TYPE_LABELS[t.debrisType as DebrisType] ?? t.debrisType ?? '-'}
                    </Badge>
                  </td>
                  <td style={{ padding: '8px 12px', textAlign: 'right' }}>
                    {t.loadPercentage ? `${t.loadPercentage}%` : '-'}
                  </td>
                  <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600 }}>
                    {t.measurementMode === 'unit'
                      ? `${t.unitCount || 0} ${DEBRIS_TYPES[t.debrisType as DebrisType]?.unitLabel || 'units'}`
                      : t.measurementMode === 'weight'
                        ? `${parseFloat(t.weight || '0').toLocaleString()} ${t.weightUnit || 'lbs'}`
                        : `${parseFloat(t.calculatedCubicYards || '0').toLocaleString()} CY`
                    }
                  </td>
                  <td style={{ padding: '8px 12px' }}>{t.zoneId ? zoneName(t.zoneId) : '-'}</td>
                  <td style={{ padding: '8px 12px' }}>{t.disposalSiteId ? siteName(t.disposalSiteId) : '-'}</td>
                  <td style={{ padding: '8px 12px' }}>
                    <Badge size="sm" color={STATUS_COLORS[t.status] ?? 'gray'}>{t.status?.replace(/_/g, ' ')}</Badge>
                  </td>
                  <td style={{ padding: '8px 12px' }}>
                    <Text size="xs">{t.pickupTime || '-'}</Text>
                    <Text size="xs" c="dimmed">{t.pickupAddress}</Text>
                  </td>
                  <td style={{ padding: '8px 12px', textAlign: 'right' }} onClick={e => e.stopPropagation()}>
                    <Group gap={4} justify="flex-end">
                      <ActionIcon size="sm" variant="filled" color="violet" title="Show QR Code"
                        onClick={async (e: React.MouseEvent) => {
                          e.stopPropagation();
                          const url = `${window.location.origin}/debris/verify/${t.id}`;
                          const dataUrl = await QRCode.toDataURL(url, { width: 250, margin: 1 });
                          setQrDataUrl(dataUrl);
                          setShowQRForTicket(t.id);
                        }}>
                        <IconPrinter size={14} />
                      </ActionIcon>
                      <ActionIcon size="sm" variant="subtle" color="violet" title="Print ticket slip with QR"
                        onClick={async () => {
                          const qrUrl = `${window.location.origin}/debris/verify/${t.id}`;
                          const qrDataUrl = await QRCode.toDataURL(qrUrl, { width: 200, margin: 1 });
                          const cfg = DEBRIS_TYPES[t.debrisType as DebrisType];
                          const measureTxt = t.measurementMode === 'unit'
                            ? `${t.unitCount || 0} ${cfg?.unitLabel || 'units'}${t.unitSubcategory ? ` (${t.unitSubcategory})` : ''}`
                            : t.measurementMode === 'weight'
                              ? `${t.weight || 0} ${t.weightUnit || 'lbs'}`
                              : `${t.loadPercentage || 0}% → ${t.calculatedCubicYards || 0} CY`;
                          const w = window.open('', '_blank');
                          if (!w) return;
                          w.document.write(`<html><head><title>Ticket ${t.ticketNumber}</title>
                            <style>body{font-family:Arial,sans-serif;padding:20px;font-size:12px;max-width:400px;margin:auto}
                            h1{font-size:18px;margin:0 0 4px}h2{font-size:14px;margin:12px 0 4px;color:#333}
                            .row{display:flex;justify-content:space-between;padding:3px 0;border-bottom:1px solid #eee}
                            .label{color:#888}.val{font-weight:600}
                            .qr{text-align:center;margin:16px 0}
                            @media print{body{padding:0}}</style>
                            </head><body>
                            <h1>Load Ticket: ${t.ticketNumber || '#' + t.id}</h1>
                            <div class="row"><span class="label">Status</span><span class="val">${t.status?.replace(/_/g, ' ')}</span></div>
                            <div class="row"><span class="label">Debris Type</span><span class="val">${DEBRIS_TYPE_LABELS[t.debrisType] || t.debrisType}</span></div>
                            <div class="row"><span class="label">Measurement</span><span class="val">${measureTxt}</span></div>
                            <div class="row"><span class="label">Truck</span><span class="val">${truckMap.get(Number(t.truckId)) || t.truckNumber || t.truckId || '-'}</span></div>
                            <div class="row"><span class="label">Pickup</span><span class="val">${t.pickupAddress || '-'}</span></div>
                            <div class="row"><span class="label">Pickup Time</span><span class="val">${t.pickupTime ? new Date(t.pickupTime).toLocaleString() : '-'}</span></div>
                            <div class="row"><span class="label">Disposal Site</span><span class="val">${t.disposalSiteId ? siteName(t.disposalSiteId) : '-'}</span></div>
                            <div class="qr"><img src="${qrDataUrl}" width="180" /><br/><small>Scan to verify at disposal site</small></div>
                            <p style="text-align:center;color:#888;font-size:10px">OpenGov EMC — Debris ADMS</p>
                            </body></html>`);
                          w.document.close();
                          w.focus();
                          w.print();
                        }}>
                        <IconPrinter size={14} />
                      </ActionIcon>
                      {canEdit && (!['verified', 'delivered', 'voided'].includes(t.status) || isAdmin) && (
                        <ActionIcon size="sm" variant="subtle"
                          color={['verified', 'delivered', 'voided'].includes(t.status) ? 'orange' : undefined}
                          title={['verified', 'delivered', 'voided'].includes(t.status) ? 'Admin Edit' : 'Edit'}
                          onClick={(e: React.MouseEvent) => { e.stopPropagation(); openEdit(t); }}>
                          <IconPencil size={14} />
                        </ActionIcon>
                      )}
                      {canEdit && (!['verified', 'delivered'].includes(t.status) || isAdmin) && (
                        <ActionIcon size="sm" variant="subtle" color="red"
                          onClick={(e: React.MouseEvent) => { e.stopPropagation(); setDeleteTarget(t); }}>
                          <IconTrash size={14} />
                        </ActionIcon>
                      )}
                      {isAdmin && t.status === 'voided' && (
                        <ActionIcon size="sm" variant="subtle" color="blue" title="Undo Void (Admin)"
                          onClick={async (e: React.MouseEvent) => {
                            e.stopPropagation();
                            await updateLoadTicket(t.id, { status: 'disputed' as any, notes: `${t.notes ? t.notes + ' | ' : ''}Void reversed by admin at ${new Date().toLocaleTimeString()}` });
                            notifications.show({ message: `${t.ticketNumber} restored — needs re-verification`, color: 'blue' });
                            loadLoadTickets();
                          }}>
                          <IconArrowBackUp size={14} />
                        </ActionIcon>
                      )}
                    </Group>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Paper>

      {totalPages > 1 && (
        <Group justify="center" mt="md" gap="xs">
          <Button variant="subtle" size="xs" disabled={page === 1} onClick={() => setPage(1)}>««</Button>
          <Button variant="subtle" size="xs" disabled={page === 1} onClick={() => setPage(p => p - 1)}>‹ Prev</Button>
          {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
            const p = totalPages <= 7 ? i + 1 : page <= 4 ? i + 1 : page >= totalPages - 3 ? totalPages - 6 + i : page - 3 + i;
            return <Button key={p} variant={p === page ? 'filled' : 'subtle'} color="blue" size="xs" onClick={() => setPage(p)}>{p}</Button>;
          })}
          <Button variant="subtle" size="xs" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>Next ›</Button>
          <Button variant="subtle" size="xs" disabled={page === totalPages} onClick={() => setPage(totalPages)}>»»</Button>
        </Group>
      )}

      {/* Create / Edit Modal */}
      <Modal opened={modalOpen} onClose={() => setModalOpen(false)}
        title={editTarget ? 'Edit Load Ticket' : 'New Load Ticket'} centered size="xl">
        <Stack>
          <Text size="sm" fw={600} c="dimmed">Ticket Info</Text>
          <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="sm">
            <TextInput label="Ticket Number" required value={form.ticketNumber ?? ''}
              onChange={e => setField('ticketNumber', e.target.value)} />
            <Select label="Status" data={STATUS_OPTIONS}
              value={(form.status as TicketStatus) ?? 'in_transit'}
              onChange={v => setField('status', v ?? 'in_transit')} />
            <Select label="Debris Type" data={DEBRIS_TYPE_SELECT}
              value={form.debrisType || null} onChange={v => setField('debrisType', v ?? '')} searchable />
          </SimpleGrid>

          <Text size="sm" fw={600} c="dimmed">Resources</Text>
          <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="sm">
            <Select label="Truck" data={truckSelect} value={form.truckId || null}
              onChange={v => handleTruckChange(v ?? '')} searchable clearable />
            <Select label="Crew" data={crewSelect} value={form.crewId || null}
              onChange={v => setField('crewId', v ?? '')} searchable clearable />
            <Select label="Contractor" data={contractorSelect} value={form.contractorId || null}
              onChange={v => setField('contractorId', v ?? '')} searchable clearable />
          </SimpleGrid>

          <SimpleGrid cols={{ base: 1, sm: editTarget ? 3 : 1 }} spacing="sm">
            <Select label="Pickup Zone" data={zoneSelect} value={form.zoneId || null}
              onChange={v => setField('zoneId', v ?? '')} searchable clearable />
            {/* Disposal site + monitor only shown when editing — at creation time
                the crew doesn't know these yet. They get set during verification. */}
            {editTarget && (
              <>
                <Select label="Disposal Site" data={siteSelect} value={form.disposalSiteId || null}
                  onChange={v => setField('disposalSiteId', v ?? '')} searchable clearable />
              </>
            )}
          </SimpleGrid>

          {/* ── Totals (auto-aggregated from stops) ─────────────── */}
          {(() => {
            const stops: any[] = (() => { try { return JSON.parse(form.pickupStopsJson || '[]'); } catch { return []; } })();
            const hasCY = parseFloat(form.calculatedCubicYards || '0') > 0;
            const hasUnits = parseFloat(form.unitCount || '0') > 0;
            const hasWeight = parseFloat(form.weight || '0') > 0;
            const hasStops = stops.length > 0;
            return (
              <>
                {(hasCY || hasUnits || hasWeight) && (
                  <>
                    <Text size="sm" fw={600} c="dimmed">Totals {hasStops ? '(from stops)' : ''}</Text>
                    <Group gap="lg">
                      {hasCY && (
                        <div>
                          <Text size="lg" fw={700}>{parseFloat(form.calculatedCubicYards || '0').toLocaleString()} CY</Text>
                          {form.truckId && form.loadPercentage && (
                            <Text size="xs" c="dimmed">{form.loadPercentage}% of truck capacity</Text>
                          )}
                        </div>
                      )}
                      {hasUnits && <Text size="lg" fw={700}>{parseFloat(form.unitCount || '0').toLocaleString()} units</Text>}
                      {hasWeight && <Text size="lg" fw={700}>{parseFloat(form.weight || '0').toLocaleString()} {form.weightUnit || 'lbs'}</Text>}
                    </Group>
                  </>
                )}
                {!hasStops && !hasCY && !hasUnits && !hasWeight && (
                  <Text size="sm" c="dimmed" fs="italic">Add pickup stops below to calculate totals</Text>
                )}
              </>
            );
          })()}

          {/* ── Pickup Stops ─────────────────────────────────── */}
          <Group justify="space-between">
            <Text size="sm" fw={600} c="dimmed">Pickup Stops</Text>
            <Button size="compact-xs" variant="light" leftSection={<IconPlus size={12} />}
              onClick={() => {
                const stops = JSON.parse(form.pickupStopsJson || '[]');
                stops.push({
                  id: `stop-${Date.now()}`,
                  address: '',
                  lat: '', lng: '',
                  debrisType: form.debrisType || '',
                  unitCount: '', unitSubcategory: '', diameterRange: '',
                  weight: '', weightUnit: 'lbs',
                  notes: '',
                  timestamp: new Date().toISOString().slice(0, 16).replace('T', ' '),
                });
                setField('pickupStopsJson', JSON.stringify(stops));
              }}>
              Add Stop
            </Button>
          </Group>
          {(() => {
            let stops: any[] = [];
            try { stops = JSON.parse(form.pickupStopsJson || '[]'); } catch { /* ignore */ }
            if (stops.length === 0) return (
              <Text size="xs" c="dimmed" ta="center" py="sm">No stops yet. Click "Add Stop" or use Field Pickup to build a load from the field.</Text>
            );
            return (
              <Stack gap="xs">
                {stops.map((stop: any, idx: number) => {
                  const stopCfg = stop.debrisType ? DEBRIS_TYPES[stop.debrisType as DebrisType] : null;
                  const updateStop = (patch: Record<string, unknown>) => {
                    const updated = [...stops];
                    updated[idx] = { ...stop, ...patch };
                    setField('pickupStopsJson', JSON.stringify(updated));
                  };
                  const hasCoords = stop.lat && stop.lng;
                  const hasBeforePhoto = !!stop.beforePhotoData;
                  const hasAfterPhoto = !!stop.afterPhotoData;
                  return (
                    <Paper key={stop.id || idx} p="sm" radius="sm" withBorder style={{ background: 'var(--mantine-color-gray-0)' }}>
                      <Group justify="space-between" mb={6}>
                        <Group gap={6}>
                          <Badge size="xs" variant="light">Stop {idx + 1}</Badge>
                          {hasCoords && <Badge size="xs" color="blue" variant="light" leftSection={<IconMapPin size={9} />}>{parseFloat(stop.lat).toFixed(4)}, {parseFloat(stop.lng).toFixed(4)}</Badge>}
                          {hasBeforePhoto && <Badge size="xs" color="green" variant="light" leftSection={<IconPhoto size={9} />}>Before</Badge>}
                          {hasAfterPhoto && <Badge size="xs" color="green" variant="light" leftSection={<IconPhoto size={9} />}>After</Badge>}
                        </Group>
                        <ActionIcon size="xs" variant="subtle" color="red" onClick={() => {
                          const updated = stops.filter((_: any, i: number) => i !== idx);
                          setField('pickupStopsJson', JSON.stringify(updated));
                        }}>
                          <IconX size={10} />
                        </ActionIcon>
                      </Group>

                      {/* Row 1: Address + GPS + Debris Type */}
                      <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="xs" mb="xs">
                        <Group gap={4} style={{ flex: 1, position: 'relative' }}>
                          <TextInput size="xs" placeholder="Address" style={{ flex: 1 }}
                            value={stop.address || ''}
                            onChange={e => {
                              updateStop({ address: e.target.value });
                              fetchStopAddr(e.target.value, idx);
                            }}
                            onFocus={() => { if (stopAddrResults.length > 0) setStopAddrActiveIdx(idx); }}
                            onBlur={() => setTimeout(() => setStopAddrActiveIdx(-1), 200)} />
                          {stopAddrActiveIdx === idx && stopAddrResults.length > 0 && (
                            <Paper withBorder shadow="md" style={{
                              position: 'absolute', top: '100%', left: 0, right: 40, zIndex: 50,
                              maxHeight: 180, overflowY: 'auto',
                            }}>
                              {stopAddrResults.map((r, ri) => (
                                <div key={ri} style={{ padding: '6px 10px', cursor: 'pointer', fontSize: 11, borderBottom: '1px solid #f0f0f0' }}
                                  onMouseDown={() => {
                                    updateStop({ address: r.label, lat: r.lat, lng: r.lng });
                                    setStopAddrResults([]);
                                    setStopAddrActiveIdx(-1);
                                  }}>
                                  {r.label}
                                </div>
                              ))}
                            </Paper>
                          )}
                          <ActionIcon size="sm" variant="light" color="blue" title="Get GPS"
                            onClick={() => {
                              if (!navigator.geolocation) { notifications.show({ message: 'GPS not available', color: 'yellow' }); return; }
                              navigator.geolocation.getCurrentPosition(
                                pos => updateStop({ lat: pos.coords.latitude.toFixed(6), lng: pos.coords.longitude.toFixed(6) }),
                                err => notifications.show({ message: `GPS: ${err.message}`, color: 'red' }),
                                { enableHighAccuracy: true, timeout: 10000 },
                              );
                            }}>
                            <IconCurrentLocation size={14} />
                          </ActionIcon>
                        </Group>
                        <Select size="xs" placeholder="Debris type"
                          data={DEBRIS_TYPE_SELECT}
                          value={stop.debrisType || null}
                          onChange={v => updateStop({ debrisType: v ?? '' })} searchable />
                        {/* Every type gets a quantity/measurement field */}
                        {(!stopCfg || stopCfg.measurementMode === 'volumetric') && (
                          <TextInput size="xs" placeholder="Est. CY or # of piles" type="number"
                            value={stop.unitCount || ''}
                            onChange={e => updateStop({ unitCount: e.target.value })} />
                        )}
                        {stopCfg?.measurementMode === 'unit' && (
                          <TextInput size="xs" placeholder={`Qty (${stopCfg.unitLabel || 'units'})`} type="number"
                            value={stop.unitCount || ''}
                            onChange={e => updateStop({ unitCount: e.target.value })} />
                        )}
                        {stopCfg?.measurementMode === 'weight' && (
                          <TextInput size="xs" placeholder="Weight" type="number"
                            value={stop.weight || ''}
                            onChange={e => updateStop({ weight: e.target.value })} />
                        )}
                      </SimpleGrid>

                      {/* Row 2: Photos + Notes */}
                      <Group gap="xs" align="flex-start">
                        {/* Before photo */}
                        {hasBeforePhoto ? (
                          <div style={{ position: 'relative', width: 64, height: 48 }}>
                            <img src={stop.beforePhotoData} alt="Before"
                              style={{ width: 64, height: 48, objectFit: 'cover', borderRadius: 4 }} />
                            <ActionIcon size={14} variant="filled" color="red"
                              style={{ position: 'absolute', top: -4, right: -4 }}
                              onClick={() => updateStop({ beforePhotoData: null })}>
                              <IconX size={8} />
                            </ActionIcon>
                          </div>
                        ) : (
                          <label style={{ cursor: 'pointer' }}>
                            <Paper p={6} radius="sm" withBorder
                              style={{ width: 64, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', background: '#fff' }}>
                              <IconCamera size={16} color="#888" />
                              <Text size="xs" c="dimmed" lh={1}>Before</Text>
                            </Paper>
                            <input type="file" accept="image/*" capture="environment" style={{ display: 'none' }}
                              onChange={async e => {
                                const file = e.target.files?.[0];
                                if (!file) return;
                                const data = await fileToBase64(file);
                                updateStop({ beforePhotoData: data });
                              }} />
                          </label>
                        )}

                        {/* After photo */}
                        {hasAfterPhoto ? (
                          <div style={{ position: 'relative', width: 64, height: 48 }}>
                            <img src={stop.afterPhotoData} alt="After"
                              style={{ width: 64, height: 48, objectFit: 'cover', borderRadius: 4 }} />
                            <ActionIcon size={14} variant="filled" color="red"
                              style={{ position: 'absolute', top: -4, right: -4 }}
                              onClick={() => updateStop({ afterPhotoData: null })}>
                              <IconX size={8} />
                            </ActionIcon>
                          </div>
                        ) : (
                          <label style={{ cursor: 'pointer' }}>
                            <Paper p={6} radius="sm" withBorder
                              style={{ width: 64, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', background: '#fff' }}>
                              <IconCamera size={16} color="#888" />
                              <Text size="xs" c="dimmed" lh={1}>After</Text>
                            </Paper>
                            <input type="file" accept="image/*" capture="environment" style={{ display: 'none' }}
                              onChange={async e => {
                                const file = e.target.files?.[0];
                                if (!file) return;
                                const data = await fileToBase64(file);
                                updateStop({ afterPhotoData: data });
                              }} />
                          </label>
                        )}

                        {/* Notes inline */}
                        <TextInput size="xs" placeholder="Notes..." style={{ flex: 1 }}
                          value={stop.notes || ''}
                          onChange={e => updateStop({ notes: e.target.value })} />
                      </Group>
                    </Paper>
                  );
                })}
              </Stack>
            );
          })()}

          <TextInput label="Date" type="date"
            value={form.pickupTime ? form.pickupTime.slice(0, 10) : new Date().toISOString().slice(0, 10)}
            onChange={e => setField('pickupTime', e.target.value || '')}
            style={{ maxWidth: 220 }} />

          {/* FEMA Chain of Custody — Pickup Signatures */}
          <Divider label="Pickup — Loading Site Monitor" labelPosition="left" />
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
            <Select label="Loading Monitor" data={Array.from(new Map(monitorOptions.map(o => [o.value, o])).values())}
              value={form.loadingMonitorName || null}
              onChange={v => setField('loadingMonitorName', v ?? '')}
              searchable clearable placeholder="Select loading site monitor..." />
            <TextInput label="Load Estimate (%)" type="number" min={0} max={100}
              value={form.loadingLoadPct ?? ''}
              onChange={e => setField('loadingLoadPct', e.target.value)}
              placeholder="e.g. 75" />
          </SimpleGrid>
          <Switch
            label="Hand-loaded"
            description="FEMA auto-reduces hand-loaded trucks to 50% capacity"
            checked={form.handLoaded === 'true'}
            onChange={e => setField('handLoaded', e.currentTarget.checked ? 'true' : 'false')}
          />
          <SimpleGrid cols={2} spacing="sm">
            <SignaturePad label="Loading Monitor Signature" value={form.loadingMonitorSignature}
              onCapture={v => setField('loadingMonitorSignature', v)} />
            <SignaturePad label="Driver Signature (Pickup)" value={form.driverPickupSignature}
              onCapture={v => setField('driverPickupSignature', v)} />
          </SimpleGrid>

          <Text size="sm" fw={600} c="dimmed">Load Photo</Text>
          <div style={{ maxWidth: 200 }}>
            {loadPhoto ? (
              <div style={{ position: 'relative' }}>
                <img src={loadPhoto.data} alt="Load"
                  style={{ width: '100%', borderRadius: 6, maxHeight: 120, objectFit: 'cover' }} />
                <ActionIcon size="xs" color="red" variant="filled"
                  style={{ position: 'absolute', top: 2, right: 2 }}
                  onClick={() => setLoadPhoto(null)}>
                  <IconX size={10} />
                </ActionIcon>
              </div>
            ) : form.photoLoadUrl ? (
              <div style={{ position: 'relative' }}>
                <img src={form.photoLoadUrl} alt="Load"
                  style={{ width: '100%', borderRadius: 6, maxHeight: 120, objectFit: 'cover' }} />
                <Badge size="xs" style={{ position: 'absolute', bottom: 4, left: 4 }}>Saved</Badge>
              </div>
            ) : (
              <label style={{ cursor: 'pointer' }}>
                <Paper p="sm" radius="sm" withBorder
                  style={{ textAlign: 'center', background: 'var(--mantine-color-gray-0)', minHeight: 80, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
                  <IconCamera size={20} color="#888" />
                  <Text size="xs" c="dimmed">Photo of load</Text>
                </Paper>
                <input type="file" accept="image/*" capture="environment"
                  style={{ display: 'none' }}
                  onChange={e => handleLoadPhotoSelect(e.target.files?.[0] ?? null)} />
              </label>
            )}
          </div>

          <Textarea label="Notes" value={form.notes ?? ''}
            onChange={e => setField('notes', e.target.value)} autosize minRows={2} />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button loading={saving} onClick={handleSave}>
              {editTarget ? 'Save Changes' : 'Create Ticket'}
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Delete confirmation */}
      <Modal opened={!!deleteTarget} onClose={() => setDeleteTarget(null)} title="Delete Ticket" centered size="sm">
        <Stack>
          <Text size="sm">Delete ticket <strong>{deleteTarget?.ticketNumber}</strong>? This cannot be undone.</Text>
          <Group justify="flex-end">
            <Button variant="default" size="xs" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button color="red" size="xs" loading={saving} onClick={handleDelete}>Delete</Button>
          </Group>
        </Stack>
      </Modal>

      {/* QR Code Display Modal */}
      <Modal opened={!!showQRForTicket} onClose={() => setShowQRForTicket(null)}
        title="Load Ticket QR Code" centered size="sm">
        <Stack align="center" gap="md">
          {qrDataUrl && (
            <img src={qrDataUrl} alt="Ticket QR" style={{ width: 250, height: 250 }} />
          )}
          <Text size="sm" c="dimmed" ta="center">
            Show this QR code to the disposal site monitor.
            <br />They scan it to open the verification form.
          </Text>
          <Text size="xs" ff="monospace" c="dimmed">
            {showQRForTicket ? `${window.location.origin}/debris/verify/${showQRForTicket}` : ''}
          </Text>
          <Button variant="light" fullWidth onClick={() => {
            const t = filtered.find(tk => tk.id === showQRForTicket);
            if (t) window.open(`/debris/verify/${t.id}`, '_blank');
          }}>
            Open Verification Page
          </Button>
        </Stack>
      </Modal>

      {/* ── View Detail Modal (read-only) ──────────────── */}
      <Modal opened={!!viewTicket} onClose={() => setViewTicket(null)}
        title={`Ticket Detail — ${viewTicket?.ticketNumber || ''}`} centered size="lg">
        {viewTicket && (() => {
          const vt = viewTicket;
          const vtCfg = vt.debrisType ? DEBRIS_TYPES[vt.debrisType as DebrisType] : null;
          const vtStops: any[] = (() => { try { return JSON.parse(vt.pickupStopsJson || '[]'); } catch { return []; } })();
          return (
            <Stack gap="sm">
              <SimpleGrid cols={{ base: 2, sm: 3 }} spacing="xs">
                <div>
                  <Text size="xs" c="dimmed">Status</Text>
                  <Badge color={STATUS_COLORS[vt.status] ?? 'gray'}>{TICKET_STATUS_LABELS[vt.status as TicketStatus] ?? vt.status}</Badge>
                </div>
                <div>
                  <Text size="xs" c="dimmed">Debris Type</Text>
                  <Badge color={DEBRIS_TYPE_COLORS[vt.debrisType as DebrisType] ?? 'gray'}>
                    {DEBRIS_TYPE_LABELS[vt.debrisType as DebrisType] ?? vt.debrisType ?? '-'}
                  </Badge>
                </div>
                <div>
                  <Text size="xs" c="dimmed">Measurement</Text>
                  <Text size="sm" fw={600}>
                    {vt.calculatedCubicYards ? `${vt.calculatedCubicYards} CY` : ''}
                    {vt.unitCount ? `${vt.unitCount} ${vtCfg?.unitLabel || 'units'}` : ''}
                    {vt.weight ? `${vt.weight} ${vt.weightUnit || 'lbs'}` : ''}
                    {!vt.calculatedCubicYards && !vt.unitCount && !vt.weight ? '-' : ''}
                    {vt.loadPercentage ? ` (${vt.loadPercentage}%)` : ''}
                  </Text>
                </div>
                <div>
                  <Text size="xs" c="dimmed">Truck</Text>
                  <Text size="sm">{truckMap.get(Number(vt.truckId)) || vt.truckNumber || '-'}</Text>
                </div>
                <div>
                  <Text size="xs" c="dimmed">Contractor</Text>
                  <Text size="sm">{vt.contractorId ? (contractors.find(c => String(c.id) === vt.contractorId)?.companyName || vt.contractorId) : '-'}</Text>
                </div>
                <div>
                  <Text size="xs" c="dimmed">Zone</Text>
                  <Text size="sm">{vt.zoneId ? zoneName(vt.zoneId) : '-'}</Text>
                </div>
                <div>
                  <Text size="xs" c="dimmed">Disposal Site</Text>
                  <Text size="sm">{vt.disposalSiteId ? siteName(vt.disposalSiteId) : '-'}</Text>
                </div>
                <div>
                  <Text size="xs" c="dimmed">Date</Text>
                  <Text size="sm">{vt.pickupTime ? new Date(vt.pickupTime).toLocaleString() : '-'}</Text>
                </div>
                {vt.loadingMonitorName && (
                  <div>
                    <Text size="xs" c="dimmed">Loading Monitor</Text>
                    <Text size="sm">{vt.loadingMonitorName}{vt.loadingLoadPct ? ` (${vt.loadingLoadPct}%)` : ''}</Text>
                  </div>
                )}
                {vt.monitorName && (
                  <div>
                    <Text size="xs" c="dimmed">Tower Monitor</Text>
                    <Text size="sm">{vt.monitorName}{vt.towerLoadPct ? ` (${vt.towerLoadPct}%)` : ''}</Text>
                  </div>
                )}
                {vt.verifiedAt && (
                  <div>
                    <Text size="xs" c="dimmed">Verified At</Text>
                    <Text size="sm">{new Date(vt.verifiedAt).toLocaleString()}</Text>
                  </div>
                )}
                {vt.handLoaded === 'true' && (
                  <div>
                    <Text size="xs" c="dimmed">FEMA Flag</Text>
                    <Badge size="sm" color="orange">Hand-Loaded (50% cap)</Badge>
                  </div>
                )}
              </SimpleGrid>

              {/* Signatures */}
              {(vt.loadingMonitorSignature || vt.driverPickupSignature || vt.monitorSignature || vt.driverSignature) && (
                <>
                  <Divider label="Signatures" labelPosition="left" />
                  <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="xs">
                    {vt.loadingMonitorSignature && (
                      <div>
                        <Text size="xs" c="dimmed" mb={2}>Loading Monitor</Text>
                        <img src={vt.loadingMonitorSignature} alt="Loading monitor sig" style={{ maxWidth: 120, maxHeight: 50, border: '1px solid #eee', borderRadius: 4 }} />
                      </div>
                    )}
                    {vt.driverPickupSignature && (
                      <div>
                        <Text size="xs" c="dimmed" mb={2}>Driver (Pickup)</Text>
                        <img src={vt.driverPickupSignature} alt="Driver pickup sig" style={{ maxWidth: 120, maxHeight: 50, border: '1px solid #eee', borderRadius: 4 }} />
                      </div>
                    )}
                    {vt.monitorSignature && (
                      <div>
                        <Text size="xs" c="dimmed" mb={2}>Tower Monitor</Text>
                        <img src={vt.monitorSignature} alt="Tower monitor sig" style={{ maxWidth: 120, maxHeight: 50, border: '1px solid #eee', borderRadius: 4 }} />
                      </div>
                    )}
                    {vt.driverSignature && (
                      <div>
                        <Text size="xs" c="dimmed" mb={2}>Driver (Disposal)</Text>
                        <img src={vt.driverSignature} alt="Driver disposal sig" style={{ maxWidth: 120, maxHeight: 50, border: '1px solid #eee', borderRadius: 4 }} />
                      </div>
                    )}
                  </SimpleGrid>
                </>
              )}

              {vt.notes && (
                <>
                  <Divider label="Notes" labelPosition="left" />
                  <Text size="sm">{vt.notes}</Text>
                </>
              )}

              {vtStops.length > 0 && (
                <>
                  <Divider label={`Pickup Stops (${vtStops.length})`} labelPosition="left" />
                  <Stack gap={6}>
                    {vtStops.map((s: any, idx: number) => {
                      const sCfg = s.debrisType ? DEBRIS_TYPES[s.debrisType as DebrisType] : null;
                      return (
                        <Paper key={s.id || idx} p="xs" radius="sm" withBorder style={{ background: 'var(--mantine-color-gray-0)' }}>
                          <Group justify="space-between">
                            <Group gap={6}>
                              <Badge size="xs" variant="light">Stop {idx + 1}</Badge>
                              {s.debrisType && <Badge size="xs" color={DEBRIS_TYPE_COLORS[s.debrisType as DebrisType] ?? 'gray'}>{DEBRIS_TYPE_LABELS[s.debrisType as DebrisType] ?? s.debrisType}</Badge>}
                              {s.unitCount && <Text size="xs" fw={600}>{s.unitCount} {sCfg?.unitLabel || 'CY'}</Text>}
                              {s.weight && <Text size="xs" fw={600}>{s.weight} {s.weightUnit || 'lbs'}</Text>}
                            </Group>
                            {s.lat && s.lng && <Text size="xs" c="dimmed">{parseFloat(s.lat).toFixed(4)}, {parseFloat(s.lng).toFixed(4)}</Text>}
                          </Group>
                          {s.address && <Text size="xs" c="dimmed" mt={2}>{s.address}</Text>}
                          {(s.beforePhotoData || s.afterPhotoData) && (
                            <Group gap="xs" mt={6}>
                              {s.beforePhotoData && <img src={s.beforePhotoData} alt="Before" style={{ width: 80, height: 60, objectFit: 'cover', borderRadius: 4 }} />}
                              {s.afterPhotoData && <img src={s.afterPhotoData} alt="After" style={{ width: 80, height: 60, objectFit: 'cover', borderRadius: 4 }} />}
                            </Group>
                          )}
                          {s.notes && <Text size="xs" mt={2}>{s.notes}</Text>}
                        </Paper>
                      );
                    })}
                  </Stack>
                </>
              )}
            </Stack>
          );
        })()}
      </Modal>
    </div>
  );
}
